import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Check, Download, Edit, ExternalLink, Image, Palette, Plus, Share2, Trash2, Tv } from 'lucide-react';
import { api, BACKEND_URL } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { MediaPickerDialog } from './AdminShared';

const AdminAdvertising = () => {
  const [activeTab, setActiveTab] = useState('sponsors');
  const [sponsors, setSponsors] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sponsorForm, setSponsorForm] = useState({
    company_name: '',
    logo_url: '',
    click_url: '',
    budget_paid: 0,
    impressions_allocated: 0,
    placements: [],
    show_in_stream_overlay: true,
    show_on_nls_plus: false,
    show_on_radio_site: false,
    show_on_pitchfinder: false,
    notes: ''
  });

  const websitePlacements = [
    { id: 'news', label: 'News Articles' },
    { id: 'podcast', label: 'Podcast Page' },
    { id: 'radio', label: 'Radio Page' },
    { id: 'video_player', label: 'Video Player' },
  ];

  const socialPlacements = [
    { id: 'social_youtube', label: 'YouTube (16:9)' },
    { id: 'social_youtube_shorts', label: 'YouTube Shorts' },
    { id: 'social_instagram_reels', label: 'Instagram Reels' },
    { id: 'social_tiktok', label: 'TikTok' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sponsorsRes, advertisersRes, pricingRes] = await Promise.all([
        api.getManualSponsors(),
        api.getAdvertisers(),
        api.getAdPricing()
      ]);
      setSponsors(sponsorsRes.data);
      setAdvertisers(advertisersRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Error loading ad data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewSponsor = () => {
    setEditingSponsor(null);
    setSponsorForm({
      company_name: '',
      logo_url: '',
      click_url: '',
      budget_paid: 0,
      impressions_allocated: 0,
      placements: [],
      show_in_stream_overlay: true,
      show_on_nls_plus: false,
      show_on_radio_site: false,
      show_on_pitchfinder: false,
      notes: ''
    });
    setShowSponsorForm(true);
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setSponsorForm({
      company_name: sponsor.company_name || '',
      logo_url: sponsor.logo_url || '',
      click_url: sponsor.click_url || '',
      budget_paid: sponsor.budget_paid || 0,
      impressions_allocated: sponsor.impressions_allocated || 0,
      placements: sponsor.placements || [],
      show_in_stream_overlay: sponsor.show_in_stream_overlay !== false,
      show_on_nls_plus: sponsor.show_on_nls_plus || false,
      show_on_radio_site: sponsor.show_on_radio_site || false,
      show_on_pitchfinder: sponsor.show_on_pitchfinder || false,
      notes: sponsor.notes || ''
    });
    setShowSponsorForm(true);
  };

  const toggleSponsorPlacement = (placement) => {
    setSponsorForm(prev => ({
      ...prev,
      placements: prev.placements.includes(placement)
        ? prev.placements.filter(p => p !== placement)
        : [...prev.placements, placement]
    }));
  };

  const handleSaveSponsor = async () => {
    if (!sponsorForm.company_name || !sponsorForm.logo_url) {
      alert('Company name and logo are required');
      return;
    }
    setSaving(true);
    try {
      if (editingSponsor) {
        await api.updateManualSponsor(editingSponsor.id, sponsorForm);
      } else {
        await api.createManualSponsor(sponsorForm);
      }
      await loadData();
      setShowSponsorForm(false);
    } catch (error) {
      console.error('Error saving sponsor:', error);
      alert('Failed to save sponsor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSponsor = async (id) => {
    if (!window.confirm('Delete this sponsor?')) return;
    try {
      await api.deleteManualSponsor(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting sponsor:', error);
    }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      await api.updateAdPricing(pricing);
      alert('Pricing saved!');
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Advertising Management</h2>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {['sponsors', 'advertisers', 'pricing', 'embeds'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === tab 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {tab === 'sponsors' ? 'Manual Sponsors' : tab === 'advertisers' ? 'Advertisers' : tab === 'pricing' ? 'CPM Pricing' : 'Embed Codes'}
          </button>
        ))}
      </div>

      {/* Manual Sponsors Tab */}
      {activeTab === 'sponsors' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-400 text-sm">Add sponsors who paid via check or invoice (not through the self-service portal)</p>
            <Button onClick={handleNewSponsor} className="bg-green-500 hover:bg-green-600">
              <Plus className="w-4 h-4 mr-2" /> Add Sponsor
            </Button>
          </div>

          {sponsors.length === 0 ? (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="py-8 text-center">
                <p className="text-gray-400">No manual sponsors yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {sponsors.map(sponsor => (
                <Card key={sponsor.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4 flex items-center gap-4">
                    {sponsor.logo_url && (
                      <img 
                        src={resolveMediaUrl(sponsor.logo_url)} 
                        alt={sponsor.company_name} 
                        className="w-16 h-16 object-contain bg-white rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">{sponsor.company_name}</h3>
                      <p className="text-gray-400 text-sm">
                        Budget: ${sponsor.budget_paid} | Impressions: {sponsor.impressions_served}/{sponsor.impressions_allocated}
                      </p>
                      {sponsor.notes && <p className="text-gray-500 text-xs mt-1">{sponsor.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={sponsor.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                        {sponsor.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {sponsor.show_in_stream_overlay && (
                        <Badge className="bg-purple-500">Stream</Badge>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEditSponsor(sponsor)} className="border-gray-600">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteSponsor(sponsor.id)} className="border-red-600 text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Sponsor Form Dialog */}
          <Dialog open={showSponsorForm} onOpenChange={setShowSponsorForm}>
            <DialogContent className="bg-gray-800 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingSponsor ? 'Edit Sponsor' : 'Add Manual Sponsor'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Company Name *</Label>
                  <Input
                    value={sponsorForm.company_name}
                    onChange={(e) => setSponsorForm({...sponsorForm, company_name: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Logo URL *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={sponsorForm.logo_url}
                      onChange={(e) => setSponsorForm({...sponsorForm, logo_url: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                      placeholder="https://... or /api/uploads/..."
                    />
                    <Button type="button" variant="outline" onClick={() => setShowMediaPicker(true)} className="border-gray-600">
                      <Image className="w-4 h-4" />
                    </Button>
                  </div>
                  {sponsorForm.logo_url && (
                    <img src={resolveMediaUrl(sponsorForm.logo_url)} alt="Preview" className="mt-2 h-16 object-contain bg-white rounded p-1" />
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Click URL</Label>
                  <Input
                    value={sponsorForm.click_url}
                    onChange={(e) => setSponsorForm({...sponsorForm, click_url: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Budget Paid ($)</Label>
                    <Input
                      type="number"
                      value={sponsorForm.budget_paid}
                      onChange={(e) => setSponsorForm({...sponsorForm, budget_paid: parseFloat(e.target.value) || 0})}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Impressions Allocated</Label>
                    <Input
                      type="number"
                      value={sponsorForm.impressions_allocated}
                      onChange={(e) => setSponsorForm({...sponsorForm, impressions_allocated: parseInt(e.target.value) || 0})}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>

                {/* Website Placements */}
                <div>
                  <Label className="text-gray-300 text-sm">Website Placements</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {websitePlacements.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleSponsorPlacement(p.id)}
                        className={`px-3 py-2 rounded text-xs transition-colors ${
                          sponsorForm.placements.includes(p.id)
                            ? 'bg-sky-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Social Media Placements */}
                <div>
                  <Label className="text-gray-300 text-sm">Social Media Placements</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {socialPlacements.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleSponsorPlacement(p.id)}
                        className={`px-3 py-2 rounded text-xs transition-colors ${
                          sponsorForm.placements.includes(p.id)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stream & External Sites */}
                <div>
                  <Label className="text-gray-300 text-sm">Stream & External Sites</Label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sponsorForm.show_in_stream_overlay}
                        onChange={(e) => setSponsorForm({...sponsorForm, show_in_stream_overlay: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-gray-300 text-sm">Stream Overlay (Wirecast)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sponsorForm.show_on_radio_site}
                        onChange={(e) => setSponsorForm({...sponsorForm, show_on_radio_site: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-gray-300 text-sm">WPSL TV Radio Website</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sponsorForm.show_on_pitchfinder}
                        onChange={(e) => setSponsorForm({...sponsorForm, show_on_pitchfinder: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-gray-300 text-sm">Pitchfinder Website</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300">Notes</Label>
                  <Input
                    value={sponsorForm.notes}
                    onChange={(e) => setSponsorForm({...sponsorForm, notes: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Internal notes..."
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowSponsorForm(false)} className="flex-1 border-gray-600">
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSponsor} disabled={saving} className="flex-1 bg-green-500 hover:bg-green-600">
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Media Picker for Sponsor Logo */}
          <MediaPickerDialog
            open={showMediaPicker}
            onOpenChange={setShowMediaPicker}
            onSelect={(url) => setSponsorForm({...sponsorForm, logo_url: url})}
          />
        </div>
      )}

      {/* Advertisers Tab */}
      {activeTab === 'advertisers' && (
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">Self-registered advertisers from the advertiser portal</p>
          
          {advertisers.length === 0 ? (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="py-8 text-center">
                <p className="text-gray-400">No advertisers have registered yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-gray-400 py-3 px-4">Company</th>
                    <th className="text-left text-gray-400 py-3 px-4">Contact</th>
                    <th className="text-left text-gray-400 py-3 px-4">Email</th>
                    <th className="text-right text-gray-400 py-3 px-4">Balance</th>
                    <th className="text-left text-gray-400 py-3 px-4">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {advertisers.map(adv => (
                    <tr key={adv.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="text-white py-3 px-4 font-medium">{adv.company_name}</td>
                      <td className="text-gray-300 py-3 px-4">{adv.contact_name}</td>
                      <td className="text-gray-300 py-3 px-4">{adv.email}</td>
                      <td className="text-green-400 py-3 px-4 text-right font-mono">${adv.balance?.toFixed(2) || '0.00'}</td>
                      <td className="text-gray-400 py-3 px-4">{new Date(adv.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CPM Pricing Tab */}
      {activeTab === 'pricing' && pricing && (
        <div className="space-y-6">
          <p className="text-gray-400 text-sm">Set CPM (cost per thousand impressions) rates for each placement</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Website */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-sky-400 text-lg">Website</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'cpm_news', label: 'News Articles' },
                  { key: 'cpm_podcast', label: 'Podcast Page' },
                  { key: 'cpm_radio', label: 'Radio Page' },
                  { key: 'cpm_video_player', label: 'Video Player' },
                  { key: 'cpm_stream_overlay', label: 'Stream Overlay' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <Label className="text-gray-300 text-sm">{item.label}</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">$</span>
                      <Input
                        type="number"
                        step="0.5"
                        value={pricing[item.key] || 0}
                        onChange={(e) => setPricing({...pricing, [item.key]: parseFloat(e.target.value) || 0})}
                        className="w-20 bg-gray-700 border-gray-600 text-white text-right"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-purple-400 text-lg">Social Media</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'cpm_youtube_widescreen', label: 'YouTube (16:9)' },
                  { key: 'cpm_youtube_shorts', label: 'YouTube Shorts' },
                  { key: 'cpm_instagram_reels', label: 'Instagram Reels' },
                  { key: 'cpm_tiktok', label: 'TikTok' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <Label className="text-gray-300 text-sm">{item.label}</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">$</span>
                      <Input
                        type="number"
                        step="0.5"
                        value={pricing[item.key] || 0}
                        onChange={(e) => setPricing({...pricing, [item.key]: parseFloat(e.target.value) || 0})}
                        className="w-20 bg-gray-700 border-gray-600 text-white text-right"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Video & Settings */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-green-400 text-lg">Video & Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'cpm_video_30s', label: 'Video 30s' },
                  { key: 'cpm_video_60s', label: 'Video 60s' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <Label className="text-gray-300 text-sm">{item.label}</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">$</span>
                      <Input
                        type="number"
                        step="0.5"
                        value={pricing[item.key] || 0}
                        onChange={(e) => setPricing({...pricing, [item.key]: parseFloat(e.target.value) || 0})}
                        className="w-20 bg-gray-700 border-gray-600 text-white text-right"
                      />
                    </div>
                  </div>
                ))}
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300 text-sm">Min Budget</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">$</span>
                      <Input
                        type="number"
                        step="5"
                        value={pricing.minimum_budget || 50}
                        onChange={(e) => setPricing({...pricing, minimum_budget: parseFloat(e.target.value) || 50})}
                        className="w-20 bg-gray-700 border-gray-600 text-white text-right"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSavePricing} disabled={saving} className="bg-green-500 hover:bg-green-600">
              {saving ? 'Saving...' : 'Save Pricing'}
            </Button>
          </div>
        </div>
      )}

      {/* Embed Codes Tab */}
      {activeTab === 'embeds' && (
        <EmbedCodesSection />
      )}
    </div>
  );
};

// Embed Codes Section Component
const EmbedCodesSection = () => {
  const [copiedCode, setCopiedCode] = useState(null);
  const backendUrl = BACKEND_URL;

  const externalSites = [
    { id: 'radio', name: 'WPSL TV Radio', description: 'Radio streaming website' },
    { id: 'pitchfinder', name: 'Pitchfinder', description: 'Soccer field finder app' },
  ];

  const bannerSizes = [
    { name: 'Leaderboard', width: 728, height: 90 },
    { name: 'Medium Rectangle', width: 300, height: 250 },
    { name: 'Large Rectangle', width: 336, height: 280 },
    { name: 'Half Page', width: 300, height: 600 },
    { name: 'Mobile Banner', width: 320, height: 50 },
    { name: 'Skyscraper', width: 160, height: 600 },
  ];

  const generateEmbedCode = (siteId, width, height, rotating = false) => {
    const endpoint = rotating ? 'embed/ad-rotating' : 'embed/ad';
    const intervalParam = rotating ? '&interval=10' : '';
    return `<iframe src="${backendUrl}/api/${endpoint}/${siteId}?width=${width}&height=${height}${intervalParam}" width="${width}" height="${height}" frameborder="0" scrolling="no" style="border:none;overflow:hidden;"></iframe>`;
  };

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-400 text-sm">
          Use these iframe codes to display ads on your external websites. Sponsors with the corresponding site enabled will appear in these banners.
        </p>
      </div>

      {externalSites.map(site => (
        <Card key={site.id} className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-sky-400" />
              {site.name}
            </CardTitle>
            <p className="text-gray-400 text-sm">{site.description}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Static Banner */}
            <div>
              <h4 className="text-gray-300 text-sm font-medium mb-2">Static Banner (Random sponsor on load)</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {bannerSizes.slice(0, 4).map(size => {
                  const code = generateEmbedCode(site.id, size.width, size.height, false);
                  const codeId = `${site.id}-static-${size.width}x${size.height}`;
                  return (
                    <div key={codeId} className="bg-gray-900 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-300 text-sm">{size.name} ({size.width}x{size.height})</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(code, codeId)}
                          className={`border-gray-600 text-xs ${copiedCode === codeId ? 'bg-green-600 border-green-600 text-white' : ''}`}
                        >
                          {copiedCode === codeId ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                      <pre className="text-xs text-gray-500 bg-gray-950 p-2 rounded overflow-x-auto">
                        <code>{code}</code>
                      </pre>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rotating Banner */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-gray-300 text-sm font-medium mb-2">Rotating Banner (Auto-cycles every 10s)</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {bannerSizes.slice(0, 4).map(size => {
                  const code = generateEmbedCode(site.id, size.width, size.height, true);
                  const codeId = `${site.id}-rotating-${size.width}x${size.height}`;
                  return (
                    <div key={codeId} className="bg-gray-900 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-300 text-sm">{size.name} ({size.width}x{size.height})</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(code, codeId)}
                          className={`border-gray-600 text-xs ${copiedCode === codeId ? 'bg-green-600 border-green-600 text-white' : ''}`}
                        >
                          {copiedCode === codeId ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                      <pre className="text-xs text-gray-500 bg-gray-950 p-2 rounded overflow-x-auto">
                        <code>{code}</code>
                      </pre>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preview Section */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-gray-300 text-sm font-medium mb-2">Preview (300x250)</h4>
              <div className="flex gap-4">
                <div>
                  <p className="text-gray-500 text-xs mb-1">Static</p>
                  <iframe
                    src={`${backendUrl}/api/embed/ad/${site.id}?width=300&height=250`}
                    width="300"
                    height="250"
                    frameBorder="0"
                    scrolling="no"
                    style={{ border: 'none', overflow: 'hidden', borderRadius: '4px' }}
                    title={`${site.name} static preview`}
                  />
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Rotating (10s interval)</p>
                  <iframe
                    src={`${backendUrl}/api/embed/ad-rotating/${site.id}?width=300&height=250&interval=10`}
                    width="300"
                    height="250"
                    frameBorder="0"
                    scrolling="no"
                    style={{ border: 'none', overflow: 'hidden', borderRadius: '4px' }}
                    title={`${site.name} rotating preview`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Wirecast Overlay Section */}
      <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Tv className="w-5 h-5 text-purple-400" />
            Wirecast Stream Overlay
          </CardTitle>
          <p className="text-gray-400 text-sm">Browser source URL for live streaming software (Wirecast, OBS, vMix)</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-gray-300 text-sm font-medium mb-2">Overlay URL</h4>
            <p className="text-gray-500 text-xs mb-2">Add as a "Web Page" or "Browser" source in your streaming software. The overlay auto-rotates through sponsors with transparent background.</p>
            
            <div className="space-y-3">
              {[
                { name: 'Bottom Right', position: 'bottom-right' },
                { name: 'Bottom Left', position: 'bottom-left' },
                { name: 'Top Right', position: 'top-right' },
                { name: 'Bottom Center', position: 'bottom-center' },
              ].map(pos => {
                const overlayUrl = `${backendUrl}/api/ads/wirecast-overlay?position=${pos.position}&width=200&height=100&rotation=15`;
                const overlayId = `wirecast-${pos.position}`;
                return (
                  <div key={overlayId} className="bg-gray-900 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300 text-sm">{pos.name}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(overlayUrl, overlayId)}
                        className={`border-gray-600 text-xs ${copiedCode === overlayId ? 'bg-green-600 border-green-600 text-white' : ''}`}
                      >
                        {copiedCode === overlayId ? 'Copied!' : 'Copy URL'}
                      </Button>
                    </div>
                    <pre className="text-xs text-purple-400 bg-gray-950 p-2 rounded overflow-x-auto">
                      <code>{overlayUrl}</code>
                    </pre>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-gray-300 text-sm font-medium mb-2">Customization Parameters</h4>
            <div className="text-gray-500 text-xs space-y-1">
              <p><code className="bg-gray-800 px-1 rounded">position</code> - bottom-right, bottom-left, top-right, top-left, bottom-center, top-center</p>
              <p><code className="bg-gray-800 px-1 rounded">width</code> - Logo width in pixels (default: 200)</p>
              <p><code className="bg-gray-800 px-1 rounded">height</code> - Logo height in pixels (default: 100)</p>
              <p><code className="bg-gray-800 px-1 rounded">margin</code> - Distance from edges in pixels (default: 20)</p>
              <p><code className="bg-gray-800 px-1 rounded">rotation</code> - Seconds between sponsor changes (default: 15)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">How to Use</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-400 text-sm space-y-2">
          <p><strong className="text-gray-300">1.</strong> First, add sponsors in the &quot;Manual Sponsors&quot; tab and enable the appropriate external site checkbox.</p>
          <p><strong className="text-gray-300">2.</strong> Copy the iframe code for your desired banner size.</p>
          <p><strong className="text-gray-300">3.</strong> Paste the code into your website&apos;s HTML where you want the ad to appear.</p>
          <p><strong className="text-gray-300">4.</strong> Static banners show a random sponsor on each page load; Rotating banners cycle through all active sponsors.</p>
          <p><strong className="text-purple-400 mt-4"><strong>For Wirecast/OBS:</strong></strong> Add the overlay URL as a Browser Source with transparent background enabled. Size the source to match your canvas (1920x1080).</p>
          <p className="text-yellow-500 mt-4"><strong>Note:</strong> Impressions are automatically tracked when banners are displayed.</p>
        </CardContent>
      </Card>
    </div>
  );
};

// External API Section Component
const ExternalApiSection = () => {
  const [apiKeyInfo, setApiKeyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadApiKey = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${BACKEND_URL}/api/feed/api-key`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setApiKeyInfo(data);
        }
      } catch (error) {
        console.error('Error loading API key:', error);
      } finally {
        setLoading(false);
      }
    };
    loadApiKey();
  }, []);

  const copyApiKey = () => {
    if (apiKeyInfo?.api_key) {
      navigator.clipboard.writeText(apiKeyInfo.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-gray-800/50 border-purple-900/30">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Share2 className="w-5 h-5 text-purple-400" />
          External API Access (JSON Feed)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-400 text-sm">
          Share this API key with external app developers to allow them to access your games feed.
          They can integrate live scores and stream data into their own apps.
        </p>
        
        {apiKeyInfo && (
          <>
            <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
              <Label className="text-gray-300 text-sm">API Key</Label>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-gray-800 px-3 py-2 rounded text-green-400 font-mono text-sm overflow-x-auto">
                  {apiKeyInfo.api_key}
                </code>
                <Button 
                  size="sm" 
                  onClick={copyApiKey}
                  className={copied ? "bg-green-600" : "bg-purple-600 hover:bg-purple-700"}
                >
                  {copied ? <Check className="w-4 h-4" /> : "Copy"}
                </Button>
              </div>
            </div>

            <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
              <Label className="text-gray-300 text-sm">Available Endpoints</Label>
              <div className="mt-2 space-y-2">
                {apiKeyInfo.usage?.endpoints?.map((endpoint, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-gray-500">•</span>
                    <code className="text-sky-300 text-sm">{endpoint}</code>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
              <Label className="text-gray-300 text-sm">Example Usage</Label>
              <pre className="mt-2 bg-gray-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`curl -H "X-API-Key: ${apiKeyInfo.api_key}" \\
  "${BACKEND_URL}/api/feed/games"

# Filter by team
curl -H "X-API-Key: ${apiKeyInfo.api_key}" \\
  "${BACKEND_URL}/api/feed/games?team_id=TEAM_ID"

# Filter by league  
curl -H "X-API-Key: ${apiKeyInfo.api_key}" \\
  "${BACKEND_URL}/api/feed/games?league_id=LEAGUE_ID"`}
              </pre>
            </div>

            <p className="text-xs text-gray-500">
              <strong>Security:</strong> Keep this API key private. Share it only with trusted developers.
              If compromised, contact support to regenerate a new key.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Site Settings
const AdminSiteSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [media, setMedia] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerField, setMediaPickerField] = useState(null);

  useEffect(() => {
    loadSettings();
    loadMedia();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadMedia = async () => {
    try {
      const res = await api.getMedia();
      setMedia(res.data);
    } catch (error) {
      console.error('Error loading media:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(settings);
      alert('Settings saved successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error saving settings: ${detail}`);
      console.error('Save settings failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const openMediaPicker = (field) => {
    setMediaPickerField(field);
    setShowMediaPicker(true);
  };

  const selectMedia = (mediaItem) => {
    const fullUrl = resolveMediaUrl(mediaItem.url);
    setSettings({ ...settings, [mediaPickerField]: fullUrl });
    setShowMediaPicker(false);
  };

  const clearField = (field) => {
    setSettings({ ...settings, [field]: null });
  };

  if (!settings) return <div className="text-white">Loading settings...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">
          <Palette className="w-6 h-6 inline mr-2" /> Site Settings
        </h2>
        <Button onClick={handleSave} disabled={saving} className="bg-sky-400 hover:bg-sky-500 text-gray-900">
          <Check className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* General Settings */}
      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardHeader>
          <CardTitle className="text-white">General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Site Name</Label>
              <Input 
                value={settings.site_name}
                onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Tagline</Label>
              <Input 
                value={settings.site_tagline}
                onChange={(e) => setSettings({ ...settings, site_tagline: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-gray-300">Description</Label>
              <Input 
                value={settings.site_description}
                onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Footer Text</Label>
              <Input 
                value={settings.footer_text}
                onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branding & Images */}
      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardHeader>
          <CardTitle className="text-white">Branding & Images</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Logo */}
            <div>
              <Label className="text-gray-300">Logo</Label>
              <div className="flex gap-2">
                <Input 
                  value={settings.logo_url || ''}
                  onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Logo URL"
                />
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => openMediaPicker('logo_url')}>
                  <Image className="w-4 h-4" />
                </Button>
                {settings.logo_url && (
                  <Button size="sm" variant="destructive" onClick={() => clearField('logo_url')}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {settings.logo_url && (
                <img src={resolveMediaUrl(settings.logo_url)} alt="Logo preview" className="mt-2 h-12 object-contain" />
              )}
            </div>

            {/* Favicon */}
            <div>
              <Label className="text-gray-300">Favicon</Label>
              <p className="text-gray-500 text-xs mb-2">Browser tab icon (recommended: 32x32 or 64x64 PNG/ICO)</p>
              <div className="flex gap-2">
                <Input 
                  value={settings.favicon_url || ''}
                  onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Favicon URL"
                />
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => openMediaPicker('favicon_url')}>
                  <Image className="w-4 h-4" />
                </Button>
                {settings.favicon_url && (
                  <Button size="sm" variant="destructive" onClick={() => clearField('favicon_url')}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {settings.favicon_url && (
                <div className="mt-2 flex items-center gap-3">
                  <div className="bg-gray-700 p-2 rounded flex items-center gap-2">
                    <img src={resolveMediaUrl(settings.favicon_url)} alt="Favicon preview" className="w-8 h-8 object-contain" />
                    <span className="text-gray-400 text-xs">32x32</span>
                  </div>
                  <div className="bg-gray-700 p-2 rounded flex items-center gap-2">
                    <img src={resolveMediaUrl(settings.favicon_url)} alt="Favicon preview" className="w-4 h-4 object-contain" />
                    <span className="text-gray-400 text-xs">16x16</span>
                  </div>
                  <span className="text-green-400 text-xs">✓ Favicon set</span>
                </div>
              )}
            </div>

            {/* Hero Background */}
            <div>
              <Label className="text-gray-300">Hero Background</Label>
              <div className="flex gap-2">
                <Input 
                  value={settings.hero_background || ''}
                  onChange={(e) => setSettings({ ...settings, hero_background: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Background URL"
                />
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => openMediaPicker('hero_background')}>
                  <Image className="w-4 h-4" />
                </Button>
                {settings.hero_background && (
                  <Button size="sm" variant="destructive" onClick={() => clearField('hero_background')}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {settings.hero_background && (
                <img src={resolveMediaUrl(settings.hero_background)} alt="Hero preview" className="mt-2 h-20 w-full object-cover rounded" />
              )}
            </div>

            {/* Hero Overlay Opacity */}
            <div>
              <Label className="text-gray-300">Hero Overlay Opacity (%)</Label>
              <Input 
                type="number"
                min="0"
                max="100"
                value={settings.hero_overlay_opacity}
                onChange={(e) => setSettings({ ...settings, hero_overlay_opacity: parseInt(e.target.value) })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sport Category Backgrounds */}
      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardHeader>
          <CardTitle className="text-white">Sport Category Backgrounds</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {/* Soccer Background */}
            <div>
              <Label className="text-gray-300">Soccer Background</Label>
              <div className="flex gap-2">
                <Input 
                  value={settings.soccer_background || ''}
                  onChange={(e) => setSettings({ ...settings, soccer_background: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white text-sm"
                  placeholder="URL"
                />
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => openMediaPicker('soccer_background')}>
                  <Image className="w-4 h-4" />
                </Button>
              </div>
              {settings.soccer_background && (
                <img src={resolveMediaUrl(settings.soccer_background)} alt="Soccer" className="mt-2 h-16 w-full object-cover rounded" />
              )}
            </div>

            {/* Gaelic Football Background */}
            <div>
              <Label className="text-gray-300">Gaelic Football Background</Label>
              <div className="flex gap-2">
                <Input 
                  value={settings.gaelic_football_background || ''}
                  onChange={(e) => setSettings({ ...settings, gaelic_football_background: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white text-sm"
                  placeholder="URL"
                />
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => openMediaPicker('gaelic_football_background')}>
                  <Image className="w-4 h-4" />
                </Button>
              </div>
              {settings.gaelic_football_background && (
                <img src={resolveMediaUrl(settings.gaelic_football_background)} alt="Gaelic" className="mt-2 h-16 w-full object-cover rounded" />
              )}
            </div>

            {/* Hurling/Camogie Background */}
            <div>
              <Label className="text-gray-300">Hurling/Camogie Background</Label>
              <div className="flex gap-2">
                <Input 
                  value={settings.hurling_camogie_background || ''}
                  onChange={(e) => setSettings({ ...settings, hurling_camogie_background: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white text-sm"
                  placeholder="URL"
                />
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => openMediaPicker('hurling_camogie_background')}>
                  <Image className="w-4 h-4" />
                </Button>
              </div>
              {settings.hurling_camogie_background && (
                <img src={resolveMediaUrl(settings.hurling_camogie_background)} alt="Hurling" className="mt-2 h-16 w-full object-cover rounded" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Links */}
      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardHeader>
          <CardTitle className="text-white">Social Media Links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Facebook URL</Label>
              <Input 
                value={settings.social_facebook || ''}
                onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="https://facebook.com/..."
              />
            </div>
            <div>
              <Label className="text-gray-300">Twitter/X URL</Label>
              <Input 
                value={settings.social_twitter || ''}
                onChange={(e) => setSettings({ ...settings, social_twitter: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="https://twitter.com/..."
              />
            </div>
            <div>
              <Label className="text-gray-300">Instagram URL</Label>
              <Input 
                value={settings.social_instagram || ''}
                onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="https://instagram.com/..."
              />
            </div>
            <div>
              <Label className="text-gray-300">YouTube URL</Label>
              <Input 
                value={settings.social_youtube || ''}
                onChange={(e) => setSettings({ ...settings, social_youtube: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="https://youtube.com/..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-400" />
            Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-gray-300">Google Analytics ID</Label>
            <Input 
              value={settings.google_analytics_id || ''}
              onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder="G-XXXXXXXXXX"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your GA4 Measurement ID (starts with G-). Find it in Google Analytics → Admin → Data Streams.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card className="bg-gray-800/50 border-emerald-900/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-400" />
            Data Backup & Export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-400 text-sm">
            Download a complete backup of all your data including sports, leagues, teams, games, news articles, podcast episodes, advertisers, and settings.
          </p>
          <div className="flex items-center gap-4">
            <Button 
              onClick={async () => {
                try {
                  const response = await fetch(`${BACKEND_URL}/api/admin/export`);
                  const data = await response.json();
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `wpsltv_backup_${new Date().toISOString().split('T')[0]}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (error) {
                  console.error('Export failed:', error);
                  alert('Failed to export data. Please try again.');
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Full Backup (JSON)
            </Button>
            <span className="text-xs text-gray-500">
              Includes: Sports, Leagues, Teams, Games, Fixtures, News, Podcasts, Partners, Advertisers, Settings
            </span>
          </div>
        </CardContent>
      </Card>

      {/* External API Access */}
      <ExternalApiSection />

      {/* Media Picker Modal */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-4xl bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Select Image</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 max-h-96 overflow-y-auto p-2">
            {media.map((item) => (
              <div 
                key={item.id}
                className="aspect-square bg-gray-700 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-sky-400"
                onClick={() => selectMedia(item)}
              >
                <img 
                  src={`${BACKEND_URL}${item.url}`}
                  alt={item.alt_text}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {media.length === 0 && (
              <div className="col-span-4 text-center py-8 text-gray-400">
                No images in media library. Upload images first.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== ADMIN NEWS ==============

export { AdminAdvertising, EmbedCodesSection, ExternalApiSection, AdminSiteSettings };
